package com.neurobs.ccabserver.subjectsessionview.states

import com.neurobs.ccab.{DatabaseCache, MessageData, SessionDisplayData, SessionLogStorage, SessionView, SessionViewResult, SubjectSessionsData, SubjectSessionsRequestData}
import com.neurobs.js.ccab.CCABServer
import com.neurobs.js.util.ServerResponseUtil
import com.neurobs.react.{GenericState, GenericStateGenerator, RegisteredChangeAttribute, ServerDataAttribute, StateAttribute}
import com.neurobs.util.{Guid, JsonPrinter}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks._
import slinky.core.facade.ReactElement
import io.circe.generic.auto._

class SessionLogCache extends DatabaseCache[SessionLogStorage]

class SessionSelectionState
(
	val sessionsState: ServerDataAttribute[List[SessionDisplayData]],
	val selectedSessionState: StateAttribute[Option[SessionDisplayData]],
	val cacheUpdateState: RegisteredChangeAttribute,
	val sessionLogCacheState: StateAttribute[SessionLogCache],
	val openSessionLogPanel: (Guid, Long) => Unit,
	val defaultSessionId: Option[Long],
	var defaultTestRunId: Option[Long]
) extends GenericState {

	def sessionLogCache = sessionLogCacheState.value

	def firstSessionViewResult: Option[SessionViewResult] = sessionsState.value.map(_.headOption.map(_.session)).getOrElse(None)
	def withFirstSessionViewResult[T](func: SessionViewResult => T): Option[T] = firstSessionViewResult.map(func(_))

	def withSameAdmin: Option[Option[String]] = sessionsState.withData(
		sessionsData => sessionsData.headOption match {
			case None => None
			case Some(firstSession) =>
				if (sessionsData.forall(_.adminUsername == firstSession.adminUsername)) Some(firstSession.adminUsername)
				else None
		},
		None
	)

	def withSameStation: Option[String] = sessionsState.withData(
		sessionsData => sessionsData.headOption match {
			case None => None
			case Some(firstSession) =>
				if (sessionsData.forall(_.session.session.stationName == firstSession.session.session.stationName)) {
					Some(firstSession.session.session.stationName)
				} else None
		},
		None
	)

	def withSameStudy: Option[String] = sessionsState.withData(
		sessionsData => sessionsData.headOption match {
			case None => None
			case Some(firstSession) =>
				if (sessionsData.forall(_.session.session.studyDisplayName == firstSession.session.session.studyDisplayName)) {
					Some(firstSession.session.session.studyDisplayName)
				} else None
		},
		None
	)

	def firstSessionView: Option[SessionView] = withFirstSessionViewResult(_.session)
	def withFirstSessionView[T](func: SessionView => T): Option[T] = firstSessionView.map(func(_))

	def loadSessions(requestData: SubjectSessionsRequestData): Unit = {
		CCABServer.getSubjectSessions(requestData)(handleServerSessions)
	}

	def handleServerSessions = ServerResponseUtil.handleObjectResultResponse[SubjectSessionsData](
		handleObject = data => {
			val list = data.sessions.toList
			sessionsState.setData(list)
			selectedSessionState.setValue(defaultSessionId.flatMap(id => list.find(_.session.session.sessionId == id)))
		},
		onAnyError = sessionsState.setError(_)
	)

	def selectedSessionLog = selectedSessionState.value.flatMap(session => sessionLogCache.searchCache(session.session.session.sessionId))

	def loadSessionLog(sessionId: Long) = {
		CCABServer.getSessionLog(sessionId)(handleServerSessionLog(sessionId))
	}

	def handleServerSessionLog(sessionId: Long) = {
		import MessageData._
		ServerResponseUtil.handleObjectResultResponse[String](
			handleObject = sessionLogString => {
				JsonPrinter.fromJson[SessionLogStorage](sessionLogString) match {
					case None =>
						sessionLogCache.updateCache(sessionId, "There was an error parsing the SessionLogStorage database json string")
					case Some(sessionLog) =>
						sessionLogCache.updateCache(sessionId, sessionLog)
				}
				cacheUpdateState.register()
			},
			onAnyError = error => {
				sessionLogCache.updateCache(sessionId, error)
				cacheUpdateState.register()
			}
		)
	}
}

object SessionSelectionStateGenerator extends GenericStateGenerator[SessionSelectionState] {
	override def generateEmptyDefaultState(): SessionSelectionState = new SessionSelectionState(
		emptyServerDataState[List[SessionDisplayData]](),
		emptyOptionDefaultState[SessionDisplayData](),
		emptyRegisteredChangeState(),
		emptyDefaultState(new SessionLogCache()),
		(_, _) => {},
		None,
		None
	)
}

@react
object SessionSelectionStateProvider {
	case class Props(requestData: SubjectSessionsRequestData, openSessionLogPanel: (Guid, Long) => Unit, defaultSessionId: Option[Long], defaultTestRunId: Option[Long], children: ReactElement*)
	val component = FunctionalComponent[Props] { props =>

		val sessionsState = ServerDataAttribute(useState[Either[Option[String], List[SessionDisplayData]]](Left(None)))
		val selectedSessionState = StateAttribute(useState[Option[SessionDisplayData]](None))
		val cacheUpdateState = RegisteredChangeAttribute(useState(0))
		val sessionLogCacheState = StateAttribute(useState(new SessionLogCache()))

		val sessionSelectionState = new SessionSelectionState(
			sessionsState = sessionsState,
			selectedSessionState = selectedSessionState,
			cacheUpdateState = cacheUpdateState,
			sessionLogCacheState = sessionLogCacheState,
			openSessionLogPanel = props.openSessionLogPanel,
			defaultSessionId = props.defaultSessionId,
			defaultTestRunId = props.defaultTestRunId
		)

		useEffect(() => {
			sessionSelectionState.loadSessions(props.requestData)
		}, Vector())


		useEffect(() => {
			sessionSelectionState.selectedSessionState.value.map(selectedSession => {
				val sessionId = selectedSession.session.session.sessionId
				sessionSelectionState.sessionLogCache.searchCache(sessionId) match {
					case Some(_) =>
					case None =>
						sessionSelectionState.loadSessionLog(sessionId)
				}
			})

		}, Vector(sessionSelectionState.selectedSessionState.value))

		SessionSelectionStateGenerator.Provider(sessionSelectionState)(
			props.children:_*
		)
	}
}
